import { UserDto } from 'src/api/models';

export class User implements UserDto {
  id!: number;
  name!: string;
  email!: string;
  /** Primary (first) role — legacy single-role field. Prefer hasRole()/hasAnyRole(). */
  role!: UserDto['role'];
  /** Every role held in any company (union). May be absent on sessions stored before the multi-role rollout. */
  roles!: UserDto['roles'];
  /** Which roles in which companies. Absent on sessions stored before the per-company rollout. */
  companyRoles!: UserDto['companyRoles'];
  /** Home company — the default a record this user creates is filed under. */
  companyId!: number;
  token!: string;
  tokenExpiresAt!: string;
  /**
   * When the HttpOnly refresh cookie stops working — i.e. the last moment a
   * refresh can still mint a new access token. The app can't read the cookie
   * itself, so the API tells it how long the cookie lives (`refreshExpiresIn`).
   *
   * `null` marks a session that never had a cookie (admin impersonation) and so
   * can never be refreshed; `undefined` is a session stored before this field
   * existed, which is given the benefit of the doubt.
   */
  refreshExpiresAt?: string | null;
  /**
   * Signed in through the shared sign-in page: refreshes from the calculator's own cookie (`rc_rt_calculator`).
   * Absent on a `POST /auth/login` session, which refreshes from the shared `refreshToken` cookie.
   */
  sso?: boolean;
}

/**
 * Roles that work client records — leads, contacts, calls — in a company. Accounting is deliberately absent, as in
 * the API's `CLIENT_ROLES` (docs/ROLES_AND_PERMISSIONS.md §5.6), which answers the opportunity routes by it.
 */
export const CLIENT_ROLES = ['admin', 'company-admin', 'agent', 'front-desk'];

/** The subset of User the role helpers read. */
type RoleCarrier = Pick<User, 'role' | 'roles'> & Partial<Pick<User, 'companyRoles'>>;

/**
 * Every role a user holds, in any company — the union. Use it for questions
 * that are not about a particular company; `hasAnyRoleIn` for ones that are.
 */
export function getUserRoles(user: RoleCarrier | null | undefined): string[] {
  if (!user) {
    return [];
  }
  if (user.companyRoles?.length) {
    return [...new Set(user.companyRoles.map((grant) => grant.role))];
  }
  if (user.roles?.length) {
    return user.roles;
  }
  return user.role ? [user.role] : [];
}

/** True when the user holds the given role in some company. */
export function hasRole(user: RoleCarrier | null | undefined, role: string): boolean {
  return getUserRoles(user).includes(role);
}

/** True when the user holds at least one of the given roles in some company. */
export function hasAnyRole(user: RoleCarrier | null | undefined, roles: string[]): boolean {
  const userRoles = getUserRoles(user);
  return roles.some((role) => userRoles.includes(role));
}

/**
 * The roles the user holds in one company, counting all-companies grants.
 *
 * Falls back to the union for sessions stored before the per-company rollout,
 * so an old token behaves exactly as it did rather than losing access.
 */
export function getRolesForCompany(user: RoleCarrier | null | undefined, companyId: number): string[] {
  const grants = user?.companyRoles;
  if (!grants?.length) {
    return getUserRoles(user);
  }
  return [
    ...new Set(
      grants
        .filter((grant) => grant.companyId === null || Number(grant.companyId) === Number(companyId))
        .map((grant) => grant.role),
    ),
  ];
}

/** True when the user holds at least one of `roles` in `companyId`. */
export function hasAnyRoleIn(user: RoleCarrier | null | undefined, companyId: number, roles: string[]): boolean {
  const held = getRolesForCompany(user, companyId);
  return roles.some((role) => held.includes(role));
}

/**
 * The companies this user may work in, or `null` for every company.
 *
 * `null` is the wildcard, not "none" — it is what a role granted with no
 * company (a platform admin, cross-company accounting) resolves to. An empty
 * array means no access at all. Mirrors the API helper of the same name.
 *
 * Sessions stored before the per-company rollout carry no grants; they fall
 * back to the single home company, which is what they always had.
 */
export function getAccessibleCompanyIds(
  user: (RoleCarrier & { companyId?: number | null }) | null | undefined,
): number[] | null {
  if (!user) {
    return [];
  }
  const grants = user.companyRoles;
  if (!grants?.length) {
    return user.companyId === null || user.companyId === undefined ? [] : [Number(user.companyId)];
  }
  if (grants.some((grant) => grant.companyId === null || grant.companyId === undefined)) {
    return null;
  }
  return [...new Set(grants.map((grant) => Number(grant.companyId)))];
}
